import type { FC } from 'react';

import { ComponentContainer } from '../../../../ui/component/component-container/component-container';
import { APP_CONFIG_V2_EXTERNAL_ID } from '../../../constants';
import { useUpsertAppConfigMutation } from '../../../hooks/use-mutation/use-upsert-app-config-mutation/use-upsert-app-config-mutation';
import { useAppConfigQuery } from '../../../hooks/use-query/use-app-config-query';
import type {
  AppConfig,
  ResourceFilters,
  RootLocationDataFilters,
} from '../../../types';
import { mergeConfigFilterObjects } from '../../../utils/utils';
import * as S from '../elements';

import { DataFiltersTab } from './data-filters-tab/data-filters-tab';
import { MigrateConfig } from './migrate-config/migrate-config';

interface Props {
  hasAppConfigPermission: boolean;
  rootLocationExternalId: string;
  setEditingRootLocation: (rootLocationExternalId: string | undefined) => void;
}

export const RootLocationConfigDataFilters: FC<Props> = ({
  hasAppConfigPermission,
  rootLocationExternalId,
}) => {
  const { data: appConfig, isSuccess, isLoading } = useAppConfigQuery();
  const { mutateAsync: upsertAppConfig } = useUpsertAppConfigMutation();

  const hasNewConfig =
    appConfig?.externalId === APP_CONFIG_V2_EXTERNAL_ID && !isLoading;

  const configuredRootLocation =
    appConfig?.featureConfiguration?.rootLocationConfigurations?.find(
      (rootLocation) => rootLocation.externalId === rootLocationExternalId
    );

  const onConfigSave = async (updatedDataFilters: RootLocationDataFilters) => {
    if (!appConfig) return;

    const newAppConfig: AppConfig = {
      ...appConfig,
      featureConfiguration: {
        ...appConfig.featureConfiguration,
        rootLocationConfigurations: [
          ...(appConfig.featureConfiguration?.rootLocationConfigurations?.map(
            (rootConfig) => {
              if (
                rootConfig.externalId === configuredRootLocation?.externalId
              ) {
                return {
                  ...rootConfig,
                  dataFilters: {
                    ...rootConfig.dataFilters,
                    ...updatedDataFilters,
                  },
                };
              }
              return { ...rootConfig };
            }
          ) || []),
        ],
      },
    };

    await upsertAppConfig({
      newAppConfig,
    });
  };

  const onGeneralConfigSave = (newGeneralFilters: ResourceFilters) => {
    const oldDataFilters = configuredRootLocation?.dataFilters;
    const allFilterUpdates = {
      general: newGeneralFilters,
      assets: mergeConfigFilterObjects(
        newGeneralFilters,
        oldDataFilters?.assets,
        true
      ),
      timeseries: mergeConfigFilterObjects(
        newGeneralFilters,
        oldDataFilters?.timeseries
      ),
      files: mergeConfigFilterObjects(newGeneralFilters, oldDataFilters?.files),
    };
    onConfigSave(allFilterUpdates);
  };

  return (
    <ComponentContainer>
      {appConfig && !hasNewConfig ? (
        <MigrateConfig />
      ) : (
        <S.Tabs defaultActiveKey="general">
          <S.TabPane tabKey="general" key="general" label="General">
            <DataFiltersTab
              hasAppConfigPermission={hasAppConfigPermission}
              configuredRootLocation={configuredRootLocation}
              isLoading={isLoading}
              isSuccess={isSuccess}
              onGeneralConfigSave={onGeneralConfigSave}
              dataFiltersTab="general"
            />
          </S.TabPane>
          <S.TabPane tabKey="assets" key="assets" label="Assets">
            <DataFiltersTab
              hasAppConfigPermission={hasAppConfigPermission}
              configuredRootLocation={configuredRootLocation}
              isLoading={isLoading}
              isSuccess={isSuccess}
              onConfigSave={onConfigSave}
              dataFiltersTab="assets"
            />
          </S.TabPane>
          <S.TabPane tabKey="timeseries" key="timeseries" label="Timeseries">
            <DataFiltersTab
              hasAppConfigPermission={hasAppConfigPermission}
              configuredRootLocation={configuredRootLocation}
              isLoading={isLoading}
              isSuccess={isSuccess}
              onConfigSave={onConfigSave}
              dataFiltersTab="timeseries"
            />
          </S.TabPane>
          <S.TabPane tabKey="files" key="files" label="Files">
            <DataFiltersTab
              hasAppConfigPermission={hasAppConfigPermission}
              configuredRootLocation={configuredRootLocation}
              isLoading={isLoading}
              isSuccess={isSuccess}
              onConfigSave={onConfigSave}
              dataFiltersTab="files"
            />
          </S.TabPane>
        </S.Tabs>
      )}
    </ComponentContainer>
  );
};
